import { useState, useEffect, useCallback } from 'react';
import '@/App.css';
import { Toaster } from './components/ui/sonner';
import LoginScreen from './components/LoginScreen';
import Dashboard from './components/Dashboard';
import HotFilesBoard from './components/HotFilesBoard';
import SaleContingencies from './components/SaleContingencies';
import SalesContingenciesTracker from './components/SalesContingenciesTracker';
import GoogleReviews from './components/GoogleReviews';
import Escalations from './components/Escalations';
import History from './components/History';
import PostCallRecap from './components/PostCallRecap';
import ClaraTab from './components/ClaraTab';
import AdminPanel from './components/AdminPanel';
import Reminders from './components/Reminders';
import NotificationBell from './components/NotificationBell';
import ClaraAssistant from './components/ClaraAssistant';
import BottomNav from './components/BottomNav';
import MobileToday from './components/mobile/MobileToday';
import MobilePipeline from './components/mobile/MobilePipeline';
import MobileTasks from './components/mobile/MobileTasks';
import MobileUpdates from './components/mobile/MobileUpdates';
import { seedData, exportHotFiles, getUnreadCount } from './lib/api';
import { getUser, clearSession } from './lib/session';
import { registerServiceWorker, requestNotificationPermission, subscribeToPush } from './lib/notifications';
import { SquaresFour, Files, CalendarCheck, HouseLine, Star, Siren, ClockCounterClockwise, Microphone, List, X, Download, ShieldCheck, SignOut, GearSix, Moon, Sun, Sparkle, ArrowsInSimple, ArrowsOutSimple } from '@phosphor-icons/react';

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Command Center', icon: SquaresFour },
  { id: 'files', label: 'Hot Files', icon: Files },
  { id: 'commitment-dates', label: 'Commitment Dates', icon: CalendarCheck },
  { id: 'sales-contingencies', label: 'Sales Contingencies', icon: HouseLine },
  { id: 'escalations', label: 'Escalations', icon: Siren },
  { id: 'reviews', label: 'Reviews', icon: Star },
  { id: 'recap', label: 'Recap', icon: Microphone },
  { id: 'clara', label: 'Clara AI', icon: Sparkle },
  { id: 'history', label: 'History', icon: ClockCounterClockwise },
];

function App() {
  const [user, setUser] = useState(() => getUser());
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 1280;
  const [activeTab, setActiveTab] = useState(isMobile ? 'mobile-today' : 'dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [seeded, setSeeded] = useState(false);
  const [fileFilter, setFileFilter] = useState(null);
  const [darkMode, setDarkMode] = useState(() => typeof window !== 'undefined' && (localStorage.getItem('hc-theme') === 'dark'));
  const [miniMode, setMiniMode] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => { document.documentElement.classList.toggle('dark', darkMode); localStorage.setItem('hc-theme', darkMode ? 'dark' : 'light'); }, [darkMode]);

  // Poll unread count for bottom-nav badge
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const tick = async () => { try { const r = await getUnreadCount(); if (!cancelled) setUnreadCount(r.count || 0); } catch (e) {} };
    tick();
    const t = setInterval(tick, 20000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);

  const initSeed = useCallback(async () => {
    if (seeded) return;
    try { await seedData(); setSeeded(true); } catch (e) { setSeeded(true); }
  }, [seeded]);
  useEffect(() => { initSeed(); }, [initSeed]);

  const handleLogin = (userData) => {
    setUser(userData);
    setActiveTab(typeof window !== 'undefined' && window.innerWidth < 1280 ? 'mobile-today' : 'dashboard');
    // Note: push notification permission is now requested EXPLICITLY from the
    // NotificationBell "Enable phone alerts" button, not auto on login.
    registerServiceWorker();
  };
  const handleLogout = () => { clearSession(); setUser(null); };

  const handleNavigate = (tab) => { setActiveTab(tab); setFileFilter(null); setMobileMenuOpen(false); };
  const handleFilterNavigate = (tab, filter) => { setActiveTab(tab); setFileFilter(filter); setMobileMenuOpen(false); };

  const navItems = user?.is_admin ? [...NAV_ITEMS, { id: 'admin', label: 'Admin', icon: GearSix }] : NAV_ITEMS;

  const desc = {
    dashboard: `Welcome back, ${user?.name?.split(' ')[0] || 'Team'}. Here's your daily overview.`,
    files: 'Track and manage all active borrower files by team',
    'commitment-dates': 'Monitor commitment deadlines, extensions, and team standards',
    'sales-contingencies': 'Track borrowers who need to sell a home before purchasing',
    escalations: 'Escalate files to other team members for resolution',
    reviews: 'Track client reviews - prioritized by experience for best results',
    recap: 'AI post-call recap with action items and meeting history',
    clara: 'Clara AI — craft legendary responses, loan notes, talking points & WOW moments',
    history: 'Archive of completed and removed files',
    admin: 'Manage team access, set passcodes, and control permissions',
  };

  if (!user) return <><Toaster position="top-right" /><LoginScreen onLogin={handleLogin} /></>;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Toaster position="top-right" />
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-md border-b border-border" data-testid="main-nav">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <img src="/icons/mr-daily-tile.png" alt="MR Daily" className="w-10 h-10 rounded-md border border-border bg-white shadow-sm" style={{ letterSpacing: '-0.02em' }} />
              <div className="hidden sm:block">
                <h1 className="text-base font-extrabold tracking-tight leading-tight text-foreground">MR Daily</h1>
                <p className="mr-eyebrow leading-none mt-0.5" style={{ fontSize: '9px', letterSpacing: '0.22em' }}>Eric Hamilton · Mortgages Reimagined</p>
              </div>
            </div>

            <div className="hidden xl:flex items-center gap-0.5">
              {navItems.map(item => (
                <button key={item.id} onClick={() => handleNavigate(item.id)} data-testid={`nav-${item.id}`}
                  className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                    activeTab === item.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}>
                  <item.icon size={15} weight={activeTab === item.id ? 'fill' : 'regular'} />{item.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <div className="hidden sm:flex items-center gap-2 mr-2">
                <span className="w-8 h-8 rounded-lg bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">{user.initials}</span>
                <span className="text-sm font-medium text-foreground">{user.name?.split(' ')[0]}</span>
              </div>
              <NotificationBell />
              <button onClick={() => window.open(`${window.location.origin}/widget`, 'DailyBurnWidget', 'width=380,height=520,resizable=yes,scrollbars=yes')}
                data-testid="launch-widget-btn" className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" title="Launch Desktop Widget">
                <ArrowsOutSimple size={17} />
              </button>
              <button onClick={() => setMiniMode(!miniMode)} data-testid="mini-mode-btn"
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" title={miniMode ? 'Full mode' : 'Mini mode'}>
                {miniMode ? <ArrowsOutSimple size={17} /> : <ArrowsInSimple size={17} />}
              </button>
              <button onClick={() => setDarkMode(!darkMode)} data-testid="theme-toggle" className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" title={darkMode ? 'Light mode' : 'Dark mode'}>
                {darkMode ? <Sun size={17} /> : <Moon size={17} />}
              </button>
              <button onClick={handleLogout} data-testid="logout-btn" className="p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors" title="Sign out">
                <SignOut size={17} />
              </button>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn" className="hidden xl:hidden p-2 text-muted-foreground">
                {mobileMenuOpen ? <X size={22} /> : <List size={22} />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="xl:hidden bg-card border-t border-border px-4 py-2" data-testid="mobile-nav">
            {navItems.map(item => (
              <button key={item.id} onClick={() => handleNavigate(item.id)} data-testid={`mobile-nav-${item.id}`}
                className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-base font-medium ${
                  activeTab === item.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
                }`}>
                <item.icon size={20} weight={activeTab === item.id ? 'fill' : 'regular'} />{item.label}
              </button>
            ))}
          </div>
        )}
      </nav>

      <div className="pt-14"><Reminders /></div>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-6 pb-24 xl:pb-6">
        {activeTab !== 'dashboard' && activeTab !== 'recap' && activeTab !== 'admin' && activeTab !== 'history' && !activeTab.startsWith('mobile-') && (
          <div className="mb-5 flex items-end justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground" data-testid="page-title">
                {navItems.find(n => n.id === activeTab)?.label}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">{desc[activeTab]}</p>
            </div>
            {activeTab === 'files' && (
              <a href={exportHotFiles()} target="_blank" rel="noreferrer" data-testid="export-csv-btn">
                <button className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted border border-border transition-colors">
                  <Download size={14} /> Export CSV
                </button>
              </a>
            )}
          </div>
        )}

        {activeTab === 'dashboard' && <Dashboard onNavigate={handleNavigate} onFilterNavigate={handleFilterNavigate} currentUser={user} />}
        {activeTab === 'files' && <HotFilesBoard initialFilter={fileFilter} currentUser={user} />}
        {activeTab === 'commitment-dates' && <SaleContingencies />}
        {activeTab === 'sales-contingencies' && <SalesContingenciesTracker />}
        {activeTab === 'escalations' && <Escalations />}
        {activeTab === 'reviews' && <GoogleReviews />}
        {activeTab === 'recap' && <PostCallRecap />}
        {activeTab === 'clara' && <ClaraTab currentUser={user} />}
        {activeTab === 'history' && <History />}
        {activeTab === 'admin' && user?.is_admin && <AdminPanel />}
        {/* Mobile-only screens (hidden ≥xl via conditional nav) */}
        {activeTab === 'mobile-today'    && <MobileToday    user={user} onOpenTab={handleNavigate} />}
        {activeTab === 'mobile-pipeline' && <MobilePipeline user={user} />}
        {activeTab === 'mobile-tasks'    && <MobileTasks    user={user} />}
        {activeTab === 'mobile-updates'  && <MobileUpdates  user={user} />}
      </main>
      <ClaraAssistant currentTab={activeTab} currentUser={user} />
      <BottomNav activeTab={activeTab} onNavigate={handleNavigate} isAdmin={!!user?.is_admin} unreadCount={unreadCount} />
    </div>
  );
}

export default App;
